<template>
  <div class="line-group-log">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t("__lineGroupLogManagement") }}</h2>

      <!-- 統計資訊卡片 -->
      <div class="row mb-4" v-if="stats">
        <div class="col-md-3 mb-3">
          <div class="card">
            <div class="card-body">
              <h6 class="card-title text-muted">{{ $t("__totalLogs") }}</h6>
              <h3 class="mb-0">{{ stats.total || 0 }}</h3>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card">
            <div class="card-body">
              <h6 class="card-title text-muted">{{ $t("__successLogs") }}</h6>
              <h3 class="mb-0 text-success">{{ stats.success || 0 }}</h3>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card">
            <div class="card-body">
              <h6 class="card-title text-muted">{{ $t("__failedLogs") }}</h6>
              <h3 class="mb-0 text-danger">{{ stats.failed || 0 }}</h3>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card">
            <div class="card-body">
              <h6 class="card-title text-muted">{{ $t("__pendingLogs") }}</h6>
              <h3 class="mb-0 text-warning">{{ stats.pending || 0 }}</h3>
            </div>
          </div>
        </div>
      </div>

      <!-- 篩選和搜尋 -->
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap">
        <!-- 狀態篩選 -->
        <div class="d-flex align-items-center me-2 mb-2">
          <div class="device-report-select">
            <select v-model="filters.status" class="form-select rounded-pill me-2" @change="fetchLogs">
              <option :value="null">{{ $t("__allStatus") }}</option>
              <option value="pending">{{ $t("__pending") }}</option>
              <option value="success">{{ $t("__success") }}</option>
              <option value="failed">{{ $t("__failed") }}</option>
            </select>
          </div>
        </div>

        <!-- 日期範圍選擇 -->
        <div class="d-flex align-items-center me-2 mb-2">
          <input
            type="date"
            v-model="filters.startDate"
            class="form-control rounded-pill me-2"
            @change="fetchLogs"
          />
          <span class="mx-2">{{ $t("__to") }}</span>
          <input
            type="date"
            v-model="filters.endDate"
            class="form-control rounded-pill me-2"
            @change="fetchLogs"
          />
        </div>

        <!-- 搜尋 -->
        <form @submit.prevent="searchLogs" class="me-auto me-2 mb-2">
          <div class="input-group rounded-pill">
            <input
              v-model="filters.search"
              type="text"
              class="form-control rounded-pill"
              :placeholder="$t('__searchGroupName')"
              aria-label="search"
              aria-describedby="search"
            />
            <button
              type="submit"
              class="btn btn-link px-2 text-dark"
              id="search"
            >
              <i class="bi bi-search"></i>
            </button>
            <button
              v-if="hasActiveFilters"
              type="button"
              class="btn btn-primary px-3 ms-1 rounded-pill"
              @click="clearFilters"
            >
              {{ $t("__clearFilters") }}
            </button>
          </div>
        </form>

        <!-- 批量操作 -->
        <div class="mb-2" v-if="selectedLogs.length > 0">
          <button
            class="btn btn-outline-primary rounded-pill me-2"
            @click="batchUpdateStatus('success')"
          >
            <i class="bi bi-check-circle me-1"></i>
            {{ $t("__markAsSuccess") }}
          </button>
          <button
            class="btn btn-outline-danger rounded-pill"
            @click="batchUpdateStatus('failed')"
          >
            <i class="bi bi-x-circle me-1"></i>
            {{ $t("__markAsFailed") }}
          </button>
        </div>
      </div>

      <!-- 資料表格 -->
      <div class="card">
        <div class="card-body">
          <b-table
            :responsive="$store.state.windowWidth > $store.state.breakpoints.sm"
            :stacked="$store.state.windowWidth <= $store.state.breakpoints.sm"
            striped
            borderless
            head-variant="light"
            :items="logs || []"
            :fields="fields"
            :busy="isLoading"
            show-empty
            :empty-text="$t('__noData')"
            @row-selected="onRowSelected"
            select-mode="multi"
            selectable
            :selected-variant="'primary'"
          >
            <template v-slot:head(selected)>
              <b-form-checkbox
                v-model="selectAllState"
                @change="toggleSelectAll"
                :indeterminate="selectedLogs.length > 0 && selectedLogs.length < logs.length"
              ></b-form-checkbox>
            </template>

            <template v-slot:cell(selected)="data">
              <b-form-checkbox
                v-model="selectedLogs"
                :value="data.item.id"
                @change="updateSelectAllState"
              ></b-form-checkbox>
            </template>

            <template v-slot:cell(group_name)="data">
              <div>
                <strong>{{ data.item.group_name }}</strong>
                <br>
                <small class="text-muted">{{ data.item.group_id }}</small>
              </div>
            </template>

            <template v-slot:cell(message)="data">
              <div class="text-truncate" style="max-width: 300px;" :title="data.item.message">
                {{ data.item.message }}
              </div>
            </template>

            <template v-slot:cell(created_at)="data">
              {{ formatDateTime(data.item.created_at) }}
            </template>

            <template v-slot:cell(status)="data">
              <span class="badge" :class="getStatusVariant(data.item.status_key)">
                {{ $t('__status_' + data.item.status_key) }}
              </span>
            </template>


            <template v-slot:cell(actions)="data">
              <div class="d-flex">
                <button
                  @click="showDetail(data.item)"
                  type="button"
                  class="btn btn-link px-1 text-dark"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__viewDetail')"
                >
                  <i class="bi bi-eye mx-1 fs-5"></i>
                </button>
                <button
                  v-if="data.item.status_key === 'pending'"
                  @click="updateStatus(data.item.id, 'success')"
                  type="button"
                  class="btn btn-link px-1 text-success"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__markAsSuccess')"
                >
                  <i class="bi bi-check-circle mx-1 fs-5"></i>
                </button>
                <button
                  v-if="data.item.status_key === 'pending'"
                  @click="updateStatus(data.item.id, 'failed')"
                  type="button"
                  class="btn btn-link px-1 text-danger"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__markAsFailed')"
                >
                  <i class="bi bi-x-circle mx-1 fs-5"></i>
                </button>
              </div>
            </template>
          </b-table>

          <!-- 載入中指示器 -->
          <div v-if="isLoading" class="text-center my-4">
            <b-spinner variant="primary"></b-spinner>
          </div>
        </div>
      </div>

      <!-- 分頁 -->
      <div class="row align-items-center mt-3" v-if="totalRows">
        <div class="col-sm col-12 text-right text-sm-left">
          {{
            $t("__paginationInfo", {
              startNub: (currentPage - 1) * perPage + 1,
              entNub: Math.min(currentPage * perPage, totalRows),
              sum: totalRows,
            })
          }}
        </div>
        <div class="col-sm col-12">
          <b-pagination
            v-if="totalRows > perPage"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="right"
            first-number
            last-number
            class="mb-sm-0 mb-3 mt-sm-0 mt-3"
            @change="onPageChange"
          ></b-pagination>
        </div>
      </div>
    </div>

    <!-- 詳情 Modal -->
    <div class="modal fade" id="detail-modal" tabindex="-1" aria-hidden="true" ref="detailModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('__logDetail') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedLog">
              <div class="row mb-3">
                <div class="col-sm-4 fw-bold">{{ $t("__groupName") }}:</div>
                <div class="col-sm-8">{{ selectedLog.group_name }}</div>
              </div>
              <div class="row mb-3">
                <div class="col-sm-4 fw-bold">{{ $t("__groupId") }}:</div>
                <div class="col-sm-8">{{ selectedLog.group_id }}</div>
              </div>
              <div class="row mb-3">
                <div class="col-sm-4 fw-bold">{{ $t("__status") }}:</div>
                <div class="col-sm-8">
                  <span class="badge" :class="getStatusVariant(selectedLog.status_key)">
                    {{ $t('__status_' + selectedLog.status_key) }}
                  </span>
                </div>
              </div>
              <div class="row mb-3">
                <div class="col-sm-4 fw-bold">{{ $t("__message") }}:</div>
                <div class="col-sm-8">
                  <pre class="mb-0">{{ selectedLog.message }}</pre>
                </div>
              </div>
              <div class="row mb-3" v-if="selectedLog.error">
                <div class="col-sm-4 fw-bold">{{ $t("__errorMessage") }}:</div>
                <div class="col-sm-8">
                  <pre class="mb-0 text-danger">{{ selectedLog.error }}</pre>
                </div>
              </div>
              <div class="row mb-3">
                <div class="col-sm-4 fw-bold">{{ $t("__createdAt") }}:</div>
                <div class="col-sm-8">{{ formatDateTime(selectedLog.created_at) }}</div>
              </div>
              <div class="row mb-3" v-if="selectedLog.updated_at">
                <div class="col-sm-4 fw-bold">{{ $t("__updatedAt") }}:</div>
                <div class="col-sm-8">{{ formatDateTime(selectedLog.updated_at) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Tooltip, Modal } from "bootstrap";
import i18n from "@/lang/lang.js";
import { lineGroupLog } from "@/http/api/lineGroupLog.js";
import { mapState, mapMutations } from "vuex";
import moment from "moment-timezone";

export default {
  name: "LineGroupLog",
  data() {
    return {
      logs: null,
      stats: null,
      filters: {
        status: null,
        startDate: null,
        endDate: null,
        search: null,
      },
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      isLoading: false,
      selectedLogs: [],
      selectAllState: false,
      selectedLog: null,
      bootstrap_tooltip: null,
    };
  },
  computed: {
    ...mapState(["token", "userInfo", "isSystemManage"]),
    fields() {
      return [
        {
          key: "selected",
          label: "",
          class: "align-middle text-center",
          thStyle: { width: "40px" },
        },
        {
          key: "group_name",
          label: i18n.t("__groupInfo"),
          class: "align-middle",
          sortable: true,
        },
        {
          key: "message",
          label: i18n.t("__message"),
          class: "align-middle",
        },
        {
          key: "created_at",
          label: i18n.t("__createdAt"),
          class: "align-middle",
          sortable: true,
        },
        {
          key: "status",
          label: i18n.t("__status"),
          class: "align-middle text-center",
          sortable: true,
        },
        {
          key: "actions",
          label: i18n.t("__actions"),
          class: "align-middle text-center",
          thStyle: { width: "120px" },
        },
      ];
    },
    hasActiveFilters() {
      return (
        this.filters.status ||
        this.filters.startDate ||
        this.filters.endDate ||
        this.filters.search
      );
    },
  },
  methods: {
    ...mapMutations(["Loading", "Loaded", "AlertsInfo", "Alerted"]),

    async fetchLogs() {
      this.isLoading = true;
      this.Loading();

      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          ...this.filters,
        };

        console.log('Fetching logs with params:', params);
        const response = await lineGroupLog.get(params, this.token);
        console.log('API Response:', response.data);

        if (response.status <= 201 && response.data.status === "success") {
          const newLogs = response.data.data.data || [];
          console.log('Received new logs:', newLogs.length, 'items');

          // 使用 Vue.set 確保響應式更新
          this.$set(this, 'logs', newLogs);
          this.totalRows = response.data.data.total || 0;
          this.currentPage = response.data.data.current_page || 1;
          this.selectedLogs = [];
          this.selectAllState = false;

          console.log('Updated logs state:', this.logs.length, 'items');
          if (newLogs.length > 0) {
            console.log('First item:', newLogs[0]);
            console.log('Last item:', newLogs[newLogs.length - 1]);
          }
        } else {
          this.AlertsInfo({
            state: "error",
            title: i18n.t("__error"),
            info: response.data.errMsg || i18n.t("__fetchError"),
          });
          this.Alerted();
        }
      } catch (error) {
        this.AlertsInfo({
          state: "error",
          title: i18n.t("__error"),
          info: error.message,
        });
        this.Alerted();
      } finally {
        this.isLoading = false;
        this.Loaded();
      }
    },

    async fetchStats() {
      try {
        const response = await lineGroupLog.getStats(this.filters, this.token);
        if (response.status <= 201 && response.data.status === "success") {
          this.stats = response.data.data;
        }
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      }
    },

    searchLogs() {
      this.currentPage = 1;
      this.fetchLogs();
    },

    onPageChange(page) {
      console.log('Page changed to:', page, 'current:', this.currentPage);
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.fetchLogs();
      }
    },

    clearFilters() {
      this.filters = {
        status: null,
        startDate: null,
        endDate: null,
        search: null,
      };
      this.currentPage = 1;
      this.fetchLogs();
      this.fetchStats();
    },

    async updateStatus(id, statusKey) {
      this.Loading();
      try {
        // 將 status_key 轉換為數字狀態
        const statusMap = {
          'pending': 0,
          'failed': 1,
          'success': 2
        };
        const status = statusMap[statusKey];

        const response = await lineGroupLog.updateStatus(
          id,
          { status },
          this.token
        );

        if (response.status <= 201 && response.data.status === "success") {
          this.AlertsInfo({
            state: "success",
            title: i18n.t("__success"),
            info: i18n.t("__updateSuccess"),
          });
          this.Alerted();
          this.fetchLogs();
          this.fetchStats();
        } else {
          this.AlertsInfo({
            state: "error",
            title: i18n.t("__error"),
            info: response.data.errMsg || i18n.t("__updateError"),
          });
          this.Alerted();
        }
      } catch (error) {
        this.AlertsInfo({
          state: "error",
          title: i18n.t("__error"),
          info: error.message,
        });
        this.Alerted();
      } finally {
        this.Loaded();
      }
    },

    async batchUpdateStatus(statusKey) {
      if (this.selectedLogs.length === 0) return;

      this.Loading();
      try {
        // 將 status_key 轉換為數字狀態
        const statusMap = {
          'pending': 0,
          'failed': 1,
          'success': 2
        };
        const status = statusMap[statusKey];

        const response = await lineGroupLog.batchUpdateStatus(
          { ids: this.selectedLogs, status },
          this.token
        );

        if (response.status <= 201 && response.data.status === "success") {
          this.AlertsInfo({
            state: "success",
            title: i18n.t("__success"),
            info: i18n.t("__batchUpdateSuccess"),
          });
          this.Alerted();
          this.selectedLogs = [];
          this.selectAllState = false;
          this.fetchLogs();
          this.fetchStats();
        } else {
          this.AlertsInfo({
            state: "error",
            title: i18n.t("__error"),
            info: response.data.errMsg || i18n.t("__batchUpdateError"),
          });
          this.Alerted();
        }
      } catch (error) {
        this.AlertsInfo({
          state: "error",
          title: i18n.t("__error"),
          info: error.message,
        });
        this.Alerted();
      } finally {
        this.Loaded();
      }
    },

    showDetail(log) {
      this.selectedLog = log;
      const detailModal = new Modal(this.$refs.detailModal);
      detailModal.show();
    },

    getStatusVariant(statusKey) {
      const variants = {
        pending: "bg-warning",
        success: "bg-success",
        failed: "bg-danger",
      };
      return variants[statusKey] || "bg-secondary";
    },

    formatDateTime(datetime) {
      if (!datetime) return "";
      return moment(datetime).format("YYYY-MM-DD HH:mm:ss");
    },

    onRowSelected(items) {
      this.selectedLogs = items.map(item => item.id);
    },

    toggleSelectAll(checked) {
      if (checked) {
        this.selectedLogs = this.logs.map(log => log.id);
      } else {
        this.selectedLogs = [];
      }
    },

    updateSelectAllState() {
      if (this.selectedLogs.length === 0) {
        this.selectAllState = false;
      } else if (this.selectedLogs.length === this.logs.length) {
        this.selectAllState = true;
      }
    },

    initBootstrapTooltip() {
      this.$nextTick(function () {
        this.bootstrap_tooltip = [].slice
          .call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
          .map(function (tooltipTriggerEl) {
            return new Tooltip(tooltipTriggerEl);
          });
        document
          .querySelectorAll('[data-bs-toggle="tooltip"]')
          .forEach((ele) => {
            ele.addEventListener("shown.bs.tooltip", function () {
              setTimeout(() => {
                Tooltip.getInstance(ele).hide();
              }, 600);
            });
          });
      });
    },
  },
  watch: {
    logs: {
      handler(newLogs, oldLogs) {
        this.initBootstrapTooltip();
      },
      deep: true
    },
  },
  created() {
    // this.fetchLogs();
    // this.fetchStats();
  },
  mounted() {
    this.fetchLogs();
    this.fetchStats();
  },
  beforeDestroy() {

  },
};
</script>

<style lang="scss">
.device-report-select {
  min-width: 150px;
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  background-color: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
}
</style>
